import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  TextField,
  Grid,
  Card,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import { adminMenuItems } from '../../../../../config/layout';
import PermissionGuard from '../../../../../components/PermissionGuard';
import { createDepartment, updateDepartment } from '../../../../../api/departmentApi';
import { useErrorModal } from '../../../../../hooks/useErrorModal';

const AddDepartment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const locationState = useLocation();
  const isEdit = !!id;

  const orgManagementItem = adminMenuItems.find((item) => item.id === 'org-management');

  const currentBreadcrumbs = useMemo(() => {
    const currentnavItem = orgManagementItem?.subItems?.find(
      (item) => item.id === 'org-structure'
    );
    return currentnavItem?.breadcrumbs || [];
  }, [orgManagementItem]);

  const [formData, setFormData] = useState({
    departmentName: '',
    description: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { showError, ErrorModal } = useErrorModal();

  useEffect(() => {
    if (isEdit) {
      const departmentData = locationState.state?.departmentData;
      if (departmentData) {
        setFormData({
          departmentName: departmentData.name || '',
          description: departmentData.description || '',
        });
      } else {
        // If no data provided for edit, we could fetch it here if needed
        setSnackbar({
          open: true,
          message: 'No department data provided. Redirecting...',
          severity: 'error'
        });
        setTimeout(() => navigate('/org-management/org-structure/department'), 2000);
      }
    }
  }, [isEdit, locationState.state, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.departmentName.trim()) {
      newErrors.departmentName = 'Department name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const departmentData = {
        name: formData.departmentName,
        description: formData.description,
      };

      let result;
      if (isEdit) {
        result = await updateDepartment(id, departmentData);
      } else {
        result = await createDepartment(departmentData);
      }

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Department ${isEdit ? 'updated' : 'created'} successfully!`,
          severity: 'success'
        });

        setTimeout(() => {
          navigate('/org-management/org-structure/department');
        }, 1500);
      } else {
        setSnackbar({
          open: true,
          message: result.message || `Failed to ${isEdit ? 'update' : 'create'} department`,
          severity: 'error'
        });
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: '1rem' }} />}
        sx={{
          marginBottom: 3,
          marginTop: 2,
          '& .MuiBreadcrumbs-li': {
            fontSize: '0.875rem',
          },
        }}
      >
        {currentBreadcrumbs.map((crumb) => (
          <Link
            key={crumb.path}
            onClick={() => navigate(crumb.path)}
            sx={{
              cursor: 'pointer',
              color: '#7522D1',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {crumb.label}
          </Link>
        ))}
        <Typography sx={{ color: '#030007', fontSize: '0.875rem' }}>
          {isEdit ? 'Edit Department' : 'Add Department'}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ marginBottom: 4 }}>
        <Box
          sx={{
            backgroundColor: '#F5F5F5',
            padding: '20px 24px',
            marginBottom: 3,
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#030007',
                marginBottom: 0.5,
                fontSize: '1.1rem',
              }}
            >
              {isEdit ? 'Edit Department' : 'Add Department'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#4F4937',
                fontSize: '0.9rem',
              }}
            >
              {isEdit ? 'Update your department information' : 'Add your team as per required'}
            </Typography>
          </Box>
          <PermissionGuard module="department" action={isEdit ? "edit" : "create"}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                backgroundColor: '#7522D1',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 2.5,
                paddingY: 1,
                borderRadius: 1,
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: '#5E1BA8',
                },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
            </Button>
          </PermissionGuard>
        </Box>

        <Card
          sx={{
            padding: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: 1,
          }}
        >
          <Grid container spacing={3} sx={{ marginBottom: 3 }}>
            <Grid item xs={12} md={4}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#030007',
                  marginBottom: 1,
                  fontSize: '1rem',
                }}
              >
                {isEdit ? 'Department Information' : 'Add New Department'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#4F4937',
                  fontSize: '0.9rem',
                }}
              >
                {isEdit
                  ? 'Update the department details and information as needed.'
                  : 'Create a new department by filling in the required information below. Departments help organize your company structure and manage teams effectively.'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={8} size={{ xs: 12, sm: 12, md: 8 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Department Name *"
                    name="departmentName"
                    value={formData.departmentName}
                    onChange={handleChange}
                    placeholder="Enter here"
                    variant="outlined"
                    error={!!errors.departmentName}
                    helperText={errors.departmentName}
                  />
                </Grid>

                <Grid item xs={12} size={{ xs: 12, sm: 12, md: 12 }}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter here"
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 4,
              paddingTop: 3,
              borderTop: '1px solid #E8E8E8',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate('/org-management/org-structure/department')}
              disabled={loading}
              sx={{
                borderColor: '#030007',
                color: '#030007',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 3,
                paddingY: 1.2,
                borderRadius: 1,
                '&:hover': {
                  borderColor: '#030007',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Cancel
            </Button>
            <PermissionGuard module="department" action={isEdit ? "edit" : "create"}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  backgroundColor: '#7522D1',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 600,
                  paddingX: 3,
                  paddingY: 1.2,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#5E1BA8',
                  },
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
              </Button>
            </PermissionGuard>
          </Box>
        </Card>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ErrorModal />
    </Container>
  );
};

export default AddDepartment;
